/**
 * Shared Chip prop presets for drawers and exception tables.
 *
 * Each factory returns a `ChipProps` object that callers spread into a
 * `<Chip>` so every status / owner / type pill across the app renders
 * identically:
 *
 *   <Chip {...statusPillChipProps('Open', 'success')} />
 *   <Chip {...ownerPillChipProps('fp')} />
 *
 * The `label` is intentionally part of the returned props so call sites
 * can't drift on capitalisation or punctuation.
 */
import type { ChipProps } from '@mui/material'
import { poolwerxColors } from '../../../theme/colors'

export type StatusTone = 'success' | 'warning' | 'error' | 'info' | 'neutral'

const STATUS_TONE_FG: Record<StatusTone, string> = {
  success: poolwerxColors.success,
  warning: poolwerxColors.warning,
  error: poolwerxColors.error,
  info: poolwerxColors.teal,
  neutral: poolwerxColors.grey[700],
}

export const statusPillChipProps = (
  label: string,
  tone: StatusTone = 'neutral',
): ChipProps => ({
  label,
  size: 'small',
  variant: 'outlined',
  sx: {
    height: 22,
    fontWeight: 600,
    fontSize: '0.72rem',
    color: STATUS_TONE_FG[tone],
    borderColor: STATUS_TONE_FG[tone],
    backgroundColor: '#FFFFFF',
    '& .MuiChip-icon, & .MuiChip-iconSmall': {
      marginLeft: '6px',
      marginRight: '-2px',
    },
  },
})

export const typePillChipProps = (label: string): ChipProps => ({
  label,
  size: 'small',
  variant: 'outlined',
  sx: {
    height: 22,
    fontWeight: 500,
    fontSize: '0.72rem',
    color: poolwerxColors.grey[700],
    borderColor: poolwerxColors.grey[300],
    backgroundColor: '#FFFFFF',
  },
})

export const ownerPillChipProps = (
  owner: 'fp' | 'psc',
  labelOverride?: string,
): ChipProps =>
  owner === 'fp'
    ? {
        label: labelOverride ?? 'FP fix',
        size: 'small',
        sx: {
          height: 22,
          fontWeight: 600,
          fontSize: '0.72rem',
          color: '#FFFFFF',
          backgroundColor: poolwerxColors.teal,
        },
      }
    : {
        label: labelOverride ?? 'PSC handles',
        size: 'small',
        variant: 'outlined',
        sx: {
          height: 22,
          fontWeight: 500,
          fontSize: '0.72rem',
          color: poolwerxColors.grey[700],
          borderColor: poolwerxColors.grey[400],
          backgroundColor: '#FFFFFF',
        },
      }
